//#region frontend/islands/slide-zoom.js
var SlideZoom = class extends window.HTMLElement {
	connectedCallback() {
		const controller = new AbortController();
		const { signal } = controller;
		let initRafId = null;
		const swiperEl = this.querySelector("swiper-container");
		const allSlides = Array.from(swiperEl.querySelectorAll("swiper-slide"));
		const zoomIcon = this.querySelector(".click-to-zoom-icon");
		let zoomed = false;
		let zoomSlide = null;
		let zoomImage = null;
		let pointerX = 0;
		let pointerY = 0;
		let hasPointer = false;
		let currentX = 50;
		let currentY = 50;
		let targetX = 50;
		let targetY = 50;
		let offsetX = 0;
		let offsetY = 0;
		let lastCursorX = null;
		let lastCursorY = null;
		let rafId = null;
		const ORIGIN_EASE = window.matchMedia("(prefers-reduced-motion: reduce)").matches ? 1 : .15;
		const OFFSET_DECAY = .012;
		const clampPercent = (v) => Math.min(100, Math.max(0, v));
		const setPressed = (pressed) => {
			zoomIcon?.setAttribute("aria-pressed", String(pressed));
		};
		const upgradeImage = (img) => {
			const zoomSrc = img?.closest(".swiper-zoom-container")?.dataset.zoomSrc;
			if (!zoomSrc || img.dataset.zoomUpgraded) return;
			img.dataset.zoomUpgraded = "true";
			const hiRes = new Image();
			hiRes.src = zoomSrc;
			hiRes.decode().then(() => {
				img.removeAttribute("srcset");
				img.removeAttribute("sizes");
				img.src = zoomSrc;
			}).catch(() => {
				delete img.dataset.zoomUpgraded;
			});
		};
		const setOrigin = (x, y) => {
			currentX = x;
			currentY = y;
			zoomImage.style.transformOrigin = `${x}% ${y}%`;
		};
		const getCursorPercent = () => {
			const rect = zoomSlide.getBoundingClientRect();
			return {
				x: (pointerX - rect.left) / rect.width * 100,
				y: (pointerY - rect.top) / rect.height * 100
			};
		};
		const updateTarget = () => {
			const { x, y } = getCursorPercent();
			if (lastCursorX === null) {
				offsetX = currentX - x;
				offsetY = currentY - y;
			} else {
				offsetX *= Math.max(0, 1 - Math.abs(x - lastCursorX) * OFFSET_DECAY);
				offsetY *= Math.max(0, 1 - Math.abs(y - lastCursorY) * OFFSET_DECAY);
			}
			lastCursorX = x;
			lastCursorY = y;
			targetX = clampPercent(x + offsetX);
			targetY = clampPercent(y + offsetY);
		};
		const stepZoomOrigin = () => {
			rafId = null;
			if (!zoomed) return;
			if (hasPointer) updateTarget();
			const dx = targetX - currentX;
			const dy = targetY - currentY;
			if (Math.abs(dx) < .05 && Math.abs(dy) < .05) {
				setOrigin(targetX, targetY);
				return;
			}
			setOrigin(currentX + dx * ORIGIN_EASE, currentY + dy * ORIGIN_EASE);
			rafId = requestAnimationFrame(stepZoomOrigin);
		};
		this._onZoomMove = (e) => {
			pointerX = e.clientX;
			pointerY = e.clientY;
			hasPointer = true;
			if (rafId === null) rafId = requestAnimationFrame(stepZoomOrigin);
		};
		const zoomFrame = this;
		const zoomOut = () => {
			if (!zoomed) return;
			zoomed = false;
			swiperEl.swiper.allowTouchMove = true;
			setPressed(false);
			hasPointer = false;
			cancelAnimationFrame(rafId);
			rafId = null;
			zoomSlide.removeEventListener("mousemove", this._onZoomMove);
			zoomFrame.removeEventListener("mouseleave", zoomOut);
			zoomSlide.classList.remove("zoomed");
			const image = zoomImage;
			image.addEventListener("transitionend", () => {
				if (!zoomed) image.style.transformOrigin = "center center";
			}, { once: true });
		};
		const zoomIn = (e) => {
			const currentSlide = allSlides[swiperEl.swiper?.realIndex];
			zoomed = true;
			swiperEl.swiper.allowTouchMove = false;
			setPressed(true);
			zoomSlide = currentSlide;
			zoomImage = currentSlide.querySelector("img");
			upgradeImage(zoomImage);
			if (e) {
				pointerX = e.clientX;
				pointerY = e.clientY;
				hasPointer = true;
				const { x, y } = getCursorPercent();
				offsetX = 0;
				offsetY = 0;
				lastCursorX = x;
				lastCursorY = y;
				targetX = x;
				targetY = y;
			} else {
				hasPointer = false;
				lastCursorX = null;
				lastCursorY = null;
				targetX = 50;
				targetY = 50;
			}
			setOrigin(targetX, targetY);
			currentSlide.classList.add("zoomed");
			currentSlide.addEventListener("mousemove", this._onZoomMove);
			zoomFrame.addEventListener("mouseleave", zoomOut);
		};
		const onMainClick = (e) => {
			if (zoomed) zoomOut();
			else zoomIn(e);
		};
		const onTap = (swiper, e) => swiper.zoom.toggle(e);
		const onZoomChange = (swiper, scale, imageEl) => {
			setPressed(scale > 1);
			if (scale > 1) upgradeImage(imageEl);
		};
		const onSwiperClick = (swiper, e) => onMainClick(e);
		const onKeydown = (e) => {
			if (e.key !== "Escape") return;
			if (zoomed) zoomOut();
			else if (swiperEl.swiper?.zoom?.scale > 1) swiperEl.swiper.zoom.out();
		};
		document.addEventListener("keydown", onKeydown, { signal });
		const initWhenReady = () => {
			if (!swiperEl.swiper) {
				initRafId = requestAnimationFrame(initWhenReady);
				return;
			}
			initRafId = null;
			if (window.matchMedia("(hover: hover) and (pointer: fine)").matches) {
				swiperEl.swiper.zoom?.disable();
				swiperEl.swiper.on("click", onSwiperClick);
				zoomIcon?.addEventListener("click", () => {
					if (zoomed) zoomOut();
					else zoomIn();
				}, { signal });
				swiperEl.swiper.on("realIndexChange", zoomOut);
			} else {
				swiperEl.swiper.on("tap", onTap);
				swiperEl.swiper.on("zoomChange", onZoomChange);
				zoomIcon?.addEventListener("click", () => {
					const { zoom } = swiperEl.swiper;
					if (zoom.scale > 1) zoom.out();
					else zoom.in();
				}, { signal });
			}
		};
		initRafId = requestAnimationFrame(initWhenReady);
		this._teardown = () => {
			cancelAnimationFrame(initRafId);
			cancelAnimationFrame(rafId);
			controller.abort();
			zoomSlide?.removeEventListener("mousemove", this._onZoomMove);
			zoomFrame.removeEventListener("mouseleave", zoomOut);
			swiperEl.swiper?.off("realIndexChange", zoomOut);
			swiperEl.swiper?.off("tap", onTap);
			swiperEl.swiper?.off("zoomChange", onZoomChange);
			swiperEl.swiper?.off("click", onSwiperClick);
		};
	}
	disconnectedCallback() {
		this._teardown?.();
	}
};
window.customElements.define("slide-zoom", SlideZoom);
//#endregion
